/* eslint-disable react-hooks/exhaustive-deps */
import { FormEvent, useEffect, useState } from "react";
import { Box, Button, Container, Grid, Link, Typography, useMediaQuery, useTheme } from "@mui/material";
import { AccountBalanceOutlined, CheckCircle, RadioButtonUnchecked, WorkOutline } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { useLazyGetTabsConfigQuery } from "@api/catalogs";
import { usePostValidateNextStageMutation } from "@api/applications";
import { useLazyGetDocumentAppIdQuery } from "@api/documents";
import { InactivityHandler } from "@components/InactivityHandler";
import { ProgressBar } from "@components/ProgressBar";
import CustomRightImage from "@components/CustomRightImagev2";
import { BOFU_TABS_CONFIG, STATUS_CATALOG, TAX_SYSTEM_TYPE } from "@helpers/constantes";
import useExpedienteDocuments from "@hooks/useExpedienteDocuments";
import useStatusConfig from "@hooks/useStatusConfig";
import useTofuSteps from "@hooks/useTofuSteps";
import { BofuTabDocument, DocumentResponse, TabsConfigResponseData } from "@interfaces/index";
import { ApiError } from "@interfaces/response";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { setLoanStatus } from "@store/slices/registerSlice";
import { RootState, useAppDispatch } from "@store/store";
import { UUID } from "node:crypto";
import {
  generateDocumentConfig,
  validateDocumentStructure,
  validateRequiredDocumentsAreUploaded,
} from "@components/TabBofu/functions";
import DocumentsTabBofu from "@components/TabBofu/DocumentsTabBofu";
import { CiecValidatorRequest, useCiecValidatorMutation } from "@api/sat_apis_new/satApsi";

const SCREEN_STATUS = "CSDOS";

type DocumentType = "financieros" | "actividad";

const DOCUMENT_TYPE_OPTIONS: Array<{
  value: DocumentType;
  title: string;
  description: string;
  icon: typeof AccountBalanceOutlined;
}> = [
  {
    value: "financieros",
    title: "Documentos financieros",
    description: "Estados financieros, estados de cuenta y documentos SAT.",
    icon: AccountBalanceOutlined,
  },
  {
    value: "actividad",
    title: "Documentos de actividad",
    description: "Comprobantes de operación o actividad de tu empresa.",
    icon: WorkOutline,
  },
];

const mapTabDocuments = ({
  tab,
  docsAccount,
  clientDetailsId,
}: {
  tab: TabsConfigResponseData | undefined;
  docsAccount: DocumentResponse[];
  clientDetailsId: UUID;
}): BofuTabDocument[] => {
  if (!tab?.documents?.length) {
    return [];
  }

  return tab.documents
    .filter((item) => validateDocumentStructure(item))
    .map((docItem) =>
      generateDocumentConfig({
        docItem,
        docs_account: docsAccount,
        docs_aval: [],
        clientDetailsId,
        aval: undefined,
      }),
    );
};

interface DocumentTypeOptionCardProps {
  title: string;
  description: string;
  icon: typeof AccountBalanceOutlined;
  isSelected: boolean;
  isMobileOrTablet: boolean;
  onSelect: () => void;
}

const DocumentTypeOptionCard = ({
  title,
  description,
  icon: Icon,
  isSelected,
  isMobileOrTablet,
  onSelect,
}: DocumentTypeOptionCardProps) => {
  return (
    <Box
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect();
        }
      }}
      role="button"
      tabIndex={0}
      sx={{
        p: isMobileOrTablet ? 2 : 2.5,
        border: "1px solid",
        borderColor: isSelected ? "primary.main" : "grey.300",
        borderRadius: 3,
        cursor: "pointer",
        backgroundColor: isSelected ? "rgba(82, 140, 214, 0.06)" : "common.white",
        boxShadow: isSelected ? "0px 0px 0px 1px rgba(82, 140, 214, 0.12)" : "none",
        transition: "border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          borderColor: "primary.main",
        },
      }}
    >
      <Box display="flex" alignItems="flex-start" gap={2}>
        <Box
          sx={{
            width: isMobileOrTablet ? 40 : 44,
            height: isMobileOrTablet ? 40 : 44,
            minWidth: isMobileOrTablet ? 40 : 44,
            borderRadius: 1.5,
            backgroundColor: isSelected ? "primary.main" : "grey.100",
            color: isSelected ? "common.white" : "primary.dark",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon fontSize="small" />
        </Box>

        <Box flex={1}>
          <Box
            display="flex"
            alignItems={isMobileOrTablet ? "flex-start" : "center"}
            justifyContent="space-between"
            gap={1.5}
            flexDirection={isMobileOrTablet ? "column" : "row"}
          >
            <Typography variant={isMobileOrTablet ? "body1" : "h6"} fontWeight={600} color="primary.dark">
              {title}
            </Typography>

            {isSelected ? (
              <CheckCircle sx={{ color: "primary.main", fontSize: 26 }} />
            ) : (
              <RadioButtonUnchecked sx={{ color: "grey.400", fontSize: 26 }} />
            )}
          </Box>

          <Typography
            variant={isMobileOrTablet ? "body2" : "body1"}
            mt={1}
            color="text.secondary"
            sx={{ lineHeight: 1.5 }}
          >
            {description}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const DocumentosCiec = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const { numberOfSteps, currentStep } = useTofuSteps(SCREEN_STATUS);
  const { nextStatus: naiveNextStatus } = useStatusConfig(SCREEN_STATUS);
  const { nextStatus: afterCiecStatus } = useStatusConfig(STATUS_CATALOG.consulta_ciec.code);
  // Según el producto, CSDOS (documentos_sat) puede tener o no un paso de buró (ej. CSABE)
  // antes de CIEC. Si el siguiente paso natural ya es CIEC, se salta hasta lo que sigue después
  // de CIEC (la rama de documentos no debe pasar por esa pantalla); si el siguiente paso natural
  // es otro (ej. CSABE), se respeta tal cual, sin tocar la secuencia del backend.
  const nextStatus = naiveNextStatus?.code === STATUS_CATALOG.consulta_ciec.code ? afterCiecStatus : naiveNextStatus;

  const applicationData = useSelector((state: RootState) => state.register.application);
  const personalData = useSelector((state: RootState) => state.register.personal_data);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const brand = useSelector((state: RootState) => state.brand);
  const companyData = useSelector((state: RootState) => state.register.company);

  const [getDocumentsByAppId] = useLazyGetDocumentAppIdQuery();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [getTabsConfig] = useLazyGetTabsConfigQuery();
  const [ciecValidation] = useCiecValidatorMutation();

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [docsFinancieros, setDocsFinancieros] = useState<BofuTabDocument[]>([]);
  const [docsActividad, setDocsActividad] = useState<BofuTabDocument[]>([]);
  const [selectedType, setSelectedType] = useState<DocumentType | null>(null);

  const { expedienteDocuments, setExpedienteDocuments, handleUpdateDocument, handleUpdateDocumentMultiple } =
    useExpedienteDocuments();

  const hasBothDocumentTypes = docsFinancieros.length > 0 && docsActividad.length > 0;
  const showSelector = !isLoading && selectedType === null && hasBothDocumentTypes;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    try {
      if (!nextStatus) {
        throw new Error("No se encontró el siguiente status");
      }

      setIsSubmitting(true);

      const payload_ciec: CiecValidatorRequest = {
        rfc: companyData.rfc || "",
        ciec: "",
        application_code: applicationData.company_application_code,
        client_details_id: String(applicationData.client_details_id),
        loan_application_conditions_id: String(applicationData.application_condition_id),
        loan_application_id: String(applicationData.id),
        requested_amount: Number(applicationData.loan_amount),
        requested_term: Number(applicationData.loan_term),
        validate_ciec: false,
        company_id: brand?.id ?? "",
        connection_is_mobile_app: true,
      };

      if (
        applicationData.tax_system_type === TAX_SYSTEM_TYPE.PF.code ||
        applicationData.tax_system_type === TAX_SYSTEM_TYPE.PFAE.code
      ) {
        await ciecValidation(payload_ciec).unwrap();
      }

      await triggerPostNextStage({
        applicationId: applicationData.id,
        statusCode: nextStatus.code,
        username: `TOFU - ${accountUser.email}`,
      }).unwrap();

      dispatch(setLoanStatus(nextStatus.code));
      navigate(nextStatus.url);
    } catch (error) {
      const apiError = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "DocumentosCiec-submit",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: apiError }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const applyDocumentType = (type: DocumentType, financieros: BofuTabDocument[], actividad: BofuTabDocument[]) => {
    setSelectedType(type);
    setExpedienteDocuments(type === "financieros" ? financieros : actividad);
  };

  const handleSelectDocumentType = (type: DocumentType) => {
    applyDocumentType(type, docsFinancieros, docsActividad);
  };

  const handleChangeDocumentType = () => {
    if (selectedType === "financieros") {
      setDocsFinancieros(expedienteDocuments);
    } else if (selectedType === "actividad") {
      setDocsActividad(expedienteDocuments);
    }

    setSelectedType(null);
    setExpedienteDocuments([]);
  };

  const getInitialData = async () => {
    try {
      setIsLoading(true);

      const [documents, tabsConfig] = await Promise.all([
        getDocumentsByAppId({
          loanApplicationId: applicationData.id,
          clientDetailsId: personalData.id,
          status: true,
        }).unwrap(),
        getTabsConfig({ taxcode: applicationData.type }).unwrap(),
      ]);

      const docs_account = documents.filter((doc) => doc.client_details_id === applicationData.client_details_id);
      const clientDetailsId = applicationData.client_details_id as UUID;

      const tab_documentos_financieros = tabsConfig.find(
        (tab) => tab.tabs_bofu.code === BOFU_TABS_CONFIG.tab_documentos_sat.code,
      );
      const tab_documentos_actividad = tabsConfig.find(
        (tab) => tab.tabs_bofu.code === BOFU_TABS_CONFIG.tab_documentos_actividad.code,
      );

      const financieros = mapTabDocuments({
        tab: tab_documentos_financieros,
        docsAccount: docs_account,
        clientDetailsId,
      });
      const actividad = mapTabDocuments({
        tab: tab_documentos_actividad,
        docsAccount: docs_account,
        clientDetailsId,
      });

      setDocsFinancieros(financieros);
      setDocsActividad(actividad);

      if (financieros.length > 0 && actividad.length === 0) {
        applyDocumentType("financieros", financieros, actividad);
      } else if (actividad.length > 0 && financieros.length === 0) {
        applyDocumentType("actividad", financieros, actividad);
      } else {
        setSelectedType(null);
        setExpedienteDocuments([]);
      }
    } catch (error) {
      const apiError = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "DocumentosCiec-getInitialData",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: apiError }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getInitialData();
  }, []);

  const [canContinue, setCanContinue] = useState(false);

  useEffect(() => {
    setCanContinue(
      !isLoading && !isSubmitting && !showSelector && validateRequiredDocumentsAreUploaded(expedienteDocuments),
    );
  }, [expedienteDocuments, isLoading, isSubmitting, showSelector]);

  const activeTabCode =
    selectedType === "actividad"
      ? BOFU_TABS_CONFIG.tab_documentos_actividad.code
      : BOFU_TABS_CONFIG.tab_documentos_sat.code;

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={2}>
        <InactivityHandler />

        <Box
          sx={{
            mt: 2,
            mx: { xs: 2, md: 4 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box maxWidth="sm" width="100%">
            <Typography variant="overline" fontWeight={700} letterSpacing={1.6} color="primary.main">
              EMPRESA · DOCUMENTOS
            </Typography>

            <Typography variant="tofu_title" textAlign="left" color="primary.dark">
              Sube los documentos de tu empresa
            </Typography>

            <Typography variant={isMobileOrTablet ? "body2" : "body1"} mt={1} color="primary.dark">
              {showSelector
                ? "Elige el tipo de documentos que vas a cargar. Solo necesitas subir uno de los dos."
                : "Necesitamos estos documentos para evaluar tu solicitud de crédito."}
            </Typography>
          </Box>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3, px: "0 !important" }}>
            <ProgressBar totalSteps={numberOfSteps} currentStep={currentStep} />
          </Container>

          <Container maxWidth="sm" sx={{ mt: 3, mb: 2, px: "0 !important" }}>
            {showSelector ? (
              <Box display="flex" flexDirection="column" gap={2}>
                {DOCUMENT_TYPE_OPTIONS.map((option) => (
                  <DocumentTypeOptionCard
                    key={option.value}
                    title={option.title}
                    description={option.description}
                    icon={option.icon}
                    isSelected={false}
                    isMobileOrTablet={isMobileOrTablet}
                    onSelect={() => handleSelectDocumentType(option.value)}
                  />
                ))}
              </Box>
            ) : (
              <form onSubmit={handleSubmit}>
                {hasBothDocumentTypes && selectedType && (
                  <Link
                    component="button"
                    type="button"
                    variant="body1"
                    onClick={handleChangeDocumentType}
                    sx={{ display: "block", mb: 2, textAlign: "left" }}
                  >
                    Elegir otro tipo de documentos
                  </Link>
                )}

                <DocumentsTabBofu
                  inputPrefix={`cliente`}
                  tabCode={activeTabCode}
                  documents={expedienteDocuments}
                  onChange={handleUpdateDocument}
                  onChangeMultiple={handleUpdateDocumentMultiple}
                  RFC={companyData.rfc}
                />

                <Container maxWidth="sm" sx={{ mb: 2, mt: 2, px: "0 !important" }}>
                  <Box display="flex" justifyContent="center">
                    <Button variant="default" color="primary" type="submit" disabled={!canContinue}>
                      Continuar
                    </Button>
                  </Box>
                </Container>
              </form>
            )}
          </Container>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default DocumentosCiec;
